/**
 * Phase 5: Action Plan export (CSV + PDF) for telecom_v2.
 *
 * Shape matches both:
 *   - respondent_get_action_plan
 *   - reviewer_get_action_plan
 *
 * Access control: data is fetched from the caller's existing RPC, which
 * already enforces role gating (telecom_v2 + published CAP +
 * respondent/reviewer/admin access). This exporter only formats whatever
 * the RPC returned for the current user.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export interface ActionPlanExportEvidence {
  id: string;
  link_url: string;
  created_at?: string | null;
}

export interface ActionPlanExportFinding {
  id: string;
  display_id?: string | null;
  display_seq?: number | null;
  control_code?: string | null;
  finding_title?: string | null;
  risk_level?: string | null;
  priority_level?: string | null;
  timeframe?: string | null;
  recommended_action?: string | null;
  recommended_measures?: string | null;
  owner?: string | null;
  due_date?: string | null;
  cap_status?: string | null;
  respondent_notes?: string | null;
  reviewer_comment?: string | null;
  validation_status?: string | null;
  validated_at?: string | null;
  updated_at?: string | null;
  csv_domain?: string | null;
  nist_function?: string | null;
  evidence_links?: ActionPlanExportEvidence[];
}

export interface ActionPlanExportPayload {
  assessment_id: string;
  version?: number;
  published_at?: string | null;
  findings: ActionPlanExportFinding[];
}

const HEADERS = [
  "Finding ID",
  "Finding",
  "Required Action",
  "Owner",
  "Due Date",
  "Priority",
  "Timeframe",
  "CAP Status",
  "Respondent Notes",
  "Evidence Links",
  "Reviewer Validation",
  "Reviewer Comment",
  "Last Updated",
] as const;

import { formatDueDate } from "@/lib/dueDate";

function fmtDate(v?: string | null): string {
  return formatDueDate(v);
}

function fmtDateTime(v?: string | null): string {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
}

function pickAction(f: ActionPlanExportFinding): string {
  return (f.recommended_action || f.recommended_measures || "").trim();
}

function joinEvidence(f: ActionPlanExportFinding): string {
  const links = (f.evidence_links ?? []).map((e) => e.link_url).filter(Boolean);
  return links.join("\n");
}

function lastUpdated(f: ActionPlanExportFinding): string {
  return fmtDateTime(f.validated_at || f.updated_at || null);
}

function rowFor(f: ActionPlanExportFinding): string[] {
  return [
    f.display_id ?? "",
    f.finding_title ?? "",
    pickAction(f),
    f.owner ?? "",
    fmtDate(f.due_date),
    f.priority_level ?? "",
    f.timeframe ?? "",
    f.cap_status ?? "",
    f.respondent_notes ?? "",
    joinEvidence(f),
    f.validation_status ?? "",
    f.reviewer_comment ?? "",
    lastUpdated(f),
  ];
}

function csvEscape(v: string): string {
  const needs = /[",\n\r]/.test(v);
  const s = v.replace(/"/g, '""');
  return needs ? `"${s}"` : s;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function baseFilename(payload: ActionPlanExportPayload, ext: string): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const v = payload.version ? `_v${payload.version}` : "";
  return `action-plan_${payload.assessment_id.slice(0, 8)}${v}_${stamp}.${ext}`;
}

export function exportActionPlanCsv(payload: ActionPlanExportPayload) {
  const lines: string[] = [];
  lines.push(HEADERS.map(csvEscape).join(","));
  for (const f of payload.findings ?? []) {
    lines.push(rowFor(f).map((v) => csvEscape(v ?? "")).join(","));
  }
  const csv = lines.join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, baseFilename(payload, "csv"));
}

export function exportActionPlanPdf(payload: ActionPlanExportPayload) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Corrective Action Plan", 40, 40);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(90);
  const meta = [
    payload.version ? `Version ${payload.version}` : null,
    payload.published_at ? `Published ${fmtDateTime(payload.published_at)}` : null,
    `${payload.findings?.length ?? 0} item(s)`,
    `Exported ${new Date().toLocaleString()}`,
  ]
    .filter(Boolean)
    .join("   ·   ");
  doc.text(meta, 40, 58);
  doc.setTextColor(0);

  const body = (payload.findings ?? []).map(rowFor);

  autoTable(doc, {
    startY: 78,
    head: [HEADERS as unknown as string[]],
    body,
    styles: {
      fontSize: 7.5,
      cellPadding: 4,
      overflow: "linebreak",
      valign: "top",
    },
    headStyles: {
      fillColor: [40, 40, 40],
      textColor: 255,
      fontSize: 8,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 44, fontStyle: "bold" }, // Finding ID
      1: { cellWidth: 100 }, // Finding
      2: { cellWidth: 120 }, // Required Action
      3: { cellWidth: 56 },  // Owner
      4: { cellWidth: 52 },  // Due Date
      5: { cellWidth: 50 },  // Priority
      6: { cellWidth: 48 },  // Timeframe
      7: { cellWidth: 58 },  // CAP Status
      8: { cellWidth: 92 },  // Respondent Notes
      9: { cellWidth: 100 }, // Evidence Links
      10: { cellWidth: 62 }, // Reviewer Validation
      11: { cellWidth: 84 }, // Reviewer Comment
      12: { cellWidth: 66 }, // Last Updated
    },
    margin: { left: 24, right: 24 },
    didDrawPage: () => {
      const pageH = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(120);
      const pageNum = (doc as any).internal.getNumberOfPages();
      doc.text(
        `Powered by CSVPro  |  Page ${pageNum}`,
        pageW / 2,
        pageH - 16,
        { align: "center" },
      );
      doc.setTextColor(0);
    },
  });

  doc.save(baseFilename(payload, "pdf"));
}
